import { Trans } from "@lingui/react/macro";
import { lazy, Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useDomSize } from "~/hook/base/useDomSize.ts";
import { useGeolocation } from "~/hook/base/useGeolocation.ts";
import { getDistance } from "~/service/util.ts";
import { CountUp } from "~/ui/base/CountUp.tsx";
import { createWebDraggableSheet } from "~/ui/base/WebDraggableSheet.tsx";

const Map = lazy(() =>
  import("~/ui/base/Map.tsx").then((module) => ({ default: module.Map })),
);

const DISTANCE_FALLBACK = -1;

export const WebShowGeolocationSheet = createWebDraggableSheet<{
  position: { latitude: number; longitude: number };
  title: string;
}>(({ context, titleId }) => {
  const {
    domRef,
    size: { height, width },
  } = useDomSize<HTMLDivElement>();

  const { data } = useGeolocation();

  const distance = useMemo(
    () =>
      data
        ? Math.ceil(getDistance(data.coords, context.position) * 1000)
        : DISTANCE_FALLBACK,
    [data, context.position],
  );

  return (
    <div data-testid="위치 보기">
      <h1
        className="text-lg font-semibold text-zinc-800 dark:text-zinc-100"
        id={titleId}
      >
        {context.title}
      </h1>
      <p className="flex pt-3 pb-6 text-sm text-zinc-400">
        <Trans>
          나와의 거리:{" "}
          <CountUp
            duration={1500}
            height={20}
            value={distance === DISTANCE_FALLBACK ? null : distance}
          />
          m
        </Trans>
      </p>
      <div
        className="relative aspect-square w-full overflow-hidden rounded-2xl"
        ref={domRef}
      >
        <ErrorBoundary
          fallback={
            <div className="size-full bg-zinc-100 p-5 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              <Trans>사용할 수 없는 기기입니다.</Trans>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="size-full animate-pulse bg-zinc-100 dark:bg-zinc-800" />
            }
          >
            {height > 0 && (
              <Map
                height={height}
                position={context.position}
                showCurrentPosition
                width={width}
              />
            )}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
});
